import React, { useState, useEffect, useCallback, useRef } from 'react';
import { WifiOff, Loader, RefreshCw, Droplets, CheckCircle, ExternalLink, ChevronRight, Signal } from 'lucide-react';
import { useMqttProxy } from '../mqttProxy';

const API_BASE    = '';
const RAZORPAY_KEY = 'rzp_live_Sv53lVIUyJGV0d';
const PING_TIMEOUT = 10000;
// Same Lambda/API Gateway as tickets — pricing is routed via ?resource=plan
const TICKETS_API   = 'https://g3mu2eke0l.execute-api.ap-south-1.amazonaws.com/dev_waas_customersupport/ticket';
const PLAN_API_BASE = `${TICKETS_API}?resource=plan`;

const macToLower   = (mac) => mac.toLowerCase();
const publishTopic = (cust, mac, name) => `hoags/${cust}/${macToLower(mac)}/${name}`;
const ackTopic     = (cust, mac, name) => `hoags/${cust}/${macToLower(mac)}/${name}Ack`;

const normaliseDevice = (d) => ({
  friendlyname: d.friendly_name ?? d.friendlyname ?? '',
  prodmodel:    d.model_no      ?? d.prodmodel    ?? '',
  macid:        d.mac_id        ?? d.macid        ?? '',
  prodtype:     d.prod_type     ?? d.prodtype     ?? '',
  custname:     d.cust_name     ?? d.custname     ?? '',
  userid:       d.user_name     ?? d.userid       ?? '',
  version:      d.version_no    ?? '',
  location:     d.location      ?? '',
  _status: 'inactive',
});

const PLANS = [
  { id:'test', label:'Test Plan', price:1,   duration:'1 day',   badge:'Test',       color:'#FF9F0A', accent:'#CC7A00', perMonth:'₹1/test', features:['1 day validity','5L water','For testing only'], litres:'5', days:1 },
];

// ── Plan config — single source of truth for ALL plan durations, same pattern
// as DeviceStatus.js's RechargeSheet. Add a new price_Xm field in the plan
// table + a matching entry here and it shows up automatically.
const PLAN_CONFIG = {
  '1m':  { label:'1 Month',  days:30,  litres:'10',  color:'#FF9F0A', accent:'#CC7A00' },
  '3m':  { label:'3 Months', days:90,  litres:'30',  color:'#007AFF', accent:'#0056d3' },
  '6m':  { label:'6 Months', days:180, litres:'60',  color:'#AF52DE', accent:'#8B36B0', badge:'Popular' },
  '12m': { label:'1 Year',   days:365, litres:'100', color:'#34C759', accent:'#248A3D', badge:'Best Value' },
};

// Fallback tiers shown when no custom pricing exists for this model+region
const FALLBACK_DYNAMIC_PLANS = [
  { id:'3m',  ...PLAN_CONFIG['3m'],  price:299 },
  { id:'6m',  ...PLAN_CONFIG['6m'],  price:599 },
  { id:'12m', ...PLAN_CONFIG['12m'], price:999 },
];

// Reads every `price_*` key from the custom pricing row and maps it onto
// PLAN_CONFIG — if admin sets price_1m, a "1 Month" card appears automatically.
function buildDynamicPlans(customRow) {
  if (!customRow) return null;
  const built = Object.keys(customRow)
    .filter(k => k.startsWith('price_') && customRow[k] != null)
    .map(k => {
      const dur = k.replace('price_', '');
      const cfg = PLAN_CONFIG[dur];
      if (!cfg) return null;
      return { id: dur, ...cfg, price: Number(customRow[k]) };
    })
    .filter(Boolean)
    .sort((a, b) => a.days - b.days);
  return built.length ? built : null;
}

// Adapts a PLAN_CONFIG-shaped plan into the fields <PlanCard> expects
function toCardPlan(p) {
  return {
    id: p.id, label: p.label, price: p.price, duration: `${p.days} days`,
    badge: p.badge || null, color: p.color, accent: p.accent,
    perMonth: `${p.litres}L water`,
    features: [`${p.litres}L water allowance`, `${p.days} days validity`, 'MQTT live status'],
    litres: p.litres, days: p.days,
  };
}

const PLAN_LINKS = {
  'test': 'https://rzp.io/rzp/5cXVadV',
  '3m':   'https://rzp.io/rzp/5cXVadV',
  '6m':   'https://rzp.io/rzp/L1WQeEqh',
  '1y':   'https://rzp.io/rzp/L1WQeEqh',
};

const STYLES = `
  @keyframes rcFadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes rcSpin    { to{transform:rotate(360deg)} }
  @keyframes rcPulse   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.3;transform:scale(.6)} }
  @keyframes rcRing    { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(2.2);opacity:0} }
  @keyframes rcShimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  @keyframes iosBar    { from{opacity:1} to{opacity:.15} }
  .rc-plan-card { transition: box-shadow .22s ease, transform .22s ease; }
  .rc-plan-card:hover { transform: translateY(-4px); }
  .rc-device-card { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease; }
  .rc-device-card:hover { box-shadow: 0 8px 28px rgba(0,0,0,.1) !important; }
  .rc-pay-btn { transition: all .18s ease; }
  .rc-pay-btn:hover { transform: scale(1.03); opacity: .92; }
  .rc-pay-btn:active { transform: scale(.97); }
`;

// ── WhatsApp icon path ──────────────────────────────────────────────
const WA_PATH = "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z";

// ── Helpers ─────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {
    active:  { bg:'rgba(52,199,89,.12)',  color:'#34C759', label:'Online',   pulse:true  },
    inactive:{ bg:'rgba(142,142,147,.1)', color:'#8E8E93', label:'Offline',  pulse:false },
    pinging: { bg:'rgba(255,159,10,.12)', color:'#FF9F0A', label:'Scanning', pulse:true  },
  };
  const s = map[status] || map.inactive;
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:5,padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,background:s.bg,color:s.color}}>
      <span style={{width:5,height:5,borderRadius:'50%',background:s.color,animation:s.pulse?'rcPulse 1.4s ease-in-out infinite':'none'}}/>
      {status==='pinging'&&<Loader size={9} style={{animation:'rcSpin .8s linear infinite'}}/>}
      {s.label}
    </span>
  );
}

function DeviceCard({ device, selected, onClick }) {
  const isActive  = device._status === 'active';
  const isPinging = device._status === 'pinging';
  return (
    <div className="rc-device-card" onClick={()=>isActive&&onClick(device)} style={{
      background:'#fff',borderRadius:16,padding:'16px 18px',display:'flex',alignItems:'center',gap:14,
      cursor:isActive?'pointer':'default',
      border:selected?'2px solid #007AFF':isActive?'1.5px solid rgba(52,199,89,.3)':'1.5px solid rgba(0,0,0,.07)',
      boxShadow:selected?'0 0 0 3px rgba(0,122,255,.15),0 4px 20px rgba(0,0,0,.08)':'0 2px 10px rgba(0,0,0,.05)',
      opacity:!isActive&&!isPinging?0.55:1,animation:'rcFadeUp .3s ease both',position:'relative',overflow:'hidden',
    }}>
      {selected && <div style={{position:'absolute',top:10,right:10,width:20,height:20,borderRadius:'50%',background:'#007AFF',display:'flex',alignItems:'center',justifyContent:'center'}}><CheckCircle size={13} color="#fff"/></div>}
      {isActive  && <div style={{position:'absolute',left:0,top:0,bottom:0,width:3,background:'linear-gradient(180deg,#34C759,#30D158)',borderRadius:'3px 0 0 3px'}}/>}
      <div style={{width:44,height:44,borderRadius:13,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',
        background:isActive?'linear-gradient(135deg,#34C759,#30D158)':isPinging?'linear-gradient(135deg,#FF9F0A,#FF6B00)':'linear-gradient(135deg,#e5e5ea,#d1d1d6)',
        boxShadow:isActive?'0 4px 14px rgba(52,199,89,.35)':'none',position:'relative'}}>
        <Droplets size={20} color="#fff"/>
        {isPinging && <div style={{position:'absolute',inset:-4,borderRadius:17,border:'2px solid rgba(255,159,10,.5)',animation:'rcRing 1.2s ease-out infinite'}}/>}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
          <span style={{fontWeight:700,fontSize:15,color:'#1c1c1e',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{device.friendlyname}</span>
          <StatusPill status={device._status}/>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <span style={{fontSize:11,color:'#8E8E93',fontFamily:'monospace'}}>{device.macid.toUpperCase()}</span>
          <span style={{fontSize:11,color:'#8E8E93',display:'flex',alignItems:'center',gap:3}}><Signal size={10}/> {device.custname==='Livpure'?'Havells':device.custname}</span>
        </div>
      </div>
      {isActive && !selected && <ChevronRight size={16} color="#C7C7CC"/>}
    </div>
  );
}

function PlanCard({ plan, selected, onSelect, onPay, disabled }) {
  const isSelected = selected?.id === plan.id;
  return (
    <div className="rc-plan-card" onClick={()=>!disabled&&onSelect(plan)} style={{
      background:'#fff',borderRadius:20,padding:'22px 20px',cursor:disabled?'not-allowed':'pointer',
      border:isSelected?`2px solid ${plan.color}`:'1.5px solid rgba(0,0,0,.07)',
      boxShadow:isSelected?`0 6px 28px ${plan.color}28,0 0 0 3px ${plan.color}18`:'0 2px 12px rgba(0,0,0,.06)',
      position:'relative',overflow:'hidden',opacity:disabled?0.5:1,animation:'rcFadeUp .4s ease both',
    }}>
      <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${plan.color},${plan.accent})`,borderRadius:'20px 20px 0 0'}}/>
      {plan.badge && <div style={{position:'absolute',top:12,right:12,fontSize:9,fontWeight:800,letterSpacing:.6,padding:'2px 8px',borderRadius:20,background:`linear-gradient(135deg,${plan.color},${plan.accent})`,color:'#fff',textTransform:'uppercase'}}>{plan.badge}</div>}
      {isSelected && <div style={{position:'absolute',top:12,left:12,width:22,height:22,borderRadius:'50%',background:plan.color,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:`0 2px 8px ${plan.color}50`}}><CheckCircle size={14} color="#fff"/></div>}
      <div style={{width:44,height:44,borderRadius:14,marginBottom:14,background:`linear-gradient(135deg,${plan.color}20,${plan.color}10)`,border:`1px solid ${plan.color}25`,display:'flex',alignItems:'center',justifyContent:'center'}}><Droplets size={22} color={plan.color}/></div>
      <div style={{fontSize:13,color:'#8E8E93',fontWeight:600,marginBottom:4,letterSpacing:.2}}>{plan.label.toUpperCase()}</div>
      <div style={{display:'flex',alignItems:'baseline',gap:4,marginBottom:2}}><span style={{fontSize:32,fontWeight:900,color:'#1c1c1e',letterSpacing:-.8,lineHeight:1}}>₹{plan.price}</span></div>
      <div style={{fontSize:11,color:'#8E8E93',marginBottom:16}}>{plan.perMonth} · {plan.duration}</div>
      <div style={{display:'flex',flexDirection:'column',gap:7,marginBottom:18}}>
        {plan.features.map((f,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:16,height:16,borderRadius:'50%',background:`${plan.color}15`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><CheckCircle size={10} color={plan.color}/></div>
            <span style={{fontSize:12,color:'#48484a',fontWeight:400}}>{f}</span>
          </div>
        ))}
      </div>
      <button className="rc-pay-btn" onClick={e=>{e.stopPropagation();onPay(plan);}} disabled={disabled} style={{
        width:'100%',padding:'12px 0',borderRadius:12,border:'none',
        background:isSelected?`linear-gradient(135deg,${plan.color},${plan.accent})`:'rgba(0,0,0,.05)',
        color:isSelected?'#fff':'#8E8E93',fontWeight:700,fontSize:14,
        cursor:disabled?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8,
        fontFamily:'inherit',boxShadow:isSelected?`0 4px 16px ${plan.color}40`:'none',transition:'all .2s ease',
      }}>
        <ExternalLink size={14}/>Pay ₹{plan.price}
      </button>
    </div>
  );
}

// ── Offline Recharge Modal ───────────────────────────────────────────
function OfflineRechargeModal({ onClose }) {
  const [phone,     setPhone]     = useState('');
  const [searching, setSearching] = useState(false);
  const [result,    setResult]    = useState(null); // array of matched devices
  const [notFound,  setNotFound]  = useState(false);
  const [selDevice, setSelDevice] = useState(null); // selected device when multiple
  const [selPlan,   setSelPlan]   = useState(null);
  const [sent,      setSent]      = useState(false);

  const handleSearch = async () => {
    if (phone.trim().length < 10) return;
    setSearching(true); setResult(null); setNotFound(false); setSent(false); setSelPlan(null); setSelDevice(null);
    try {
      const res  = await fetch(`${API_BASE}/api/admin-devices`, { headers:{'ngrok-skip-browser-warning':'true'} });
      const data = await res.json();
      const arr  = Array.isArray(data) ? data : (data.registrations??data.data??data.devices??[]);
      const q    = phone.trim().replace(/\D/g,'').slice(-10); // last 10 digits
      const matches = arr.filter(d => {
        const p = (d.phone_no||d.phone||'').replace(/\D/g,'').slice(-10);
        return p === q;
      });
      if (matches.length > 0) setResult(matches); else setNotFound(true);
    } catch { setNotFound(true); }
    finally   { setSearching(false); }
  };

  const handleSendLink = (plan) => {
    if (!result) return;
    const ph   = (result.phone_no||result.phone||'').replace(/\D/g,'');
    const name = result.Enduser_name || result.cust_name || 'Customer';
    const dev  = result.friendly_name || result.friendlyname || result.mac_id || '';
    const link = PLAN_LINKS[plan.id] || PLAN_LINKS['3m'];
    const msg  = encodeURIComponent(
      `Hi ${name}! \n\nYour Havells Water Purifier (${dev}) subscription needs renewal.\n\n` +
      `Plan: ${plan.label} - ₹${plan.price}\nPay here: ${link}\n\n` +
      `Your purifier will be automatically activated within minutes of payment.\n\nTeam Havells`
    );
    window.open(`https://wa.me/91${ph}?text=${msg}`, '_blank', 'noopener,noreferrer');
    setSelPlan(plan);
    setSent(true);
  };

  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, zIndex:9999,
      background:'rgba(0,0,0,0.55)', backdropFilter:'blur(16px)',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'#fff', borderRadius:24, padding:'28px 24px',
        width:420, maxWidth:'94vw', maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 32px 80px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
          <div style={{width:44,height:44,borderRadius:13,flexShrink:0,background:'linear-gradient(135deg,#25D366,#128C7E)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 4px 14px rgba(37,211,102,.4)'}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d={WA_PATH}/></svg>
          </div>
          <div>
            <div style={{fontSize:17,fontWeight:800,color:'#1c1c1e',letterSpacing:-.3}}>Offline Recharge</div>
            <div style={{fontSize:12,color:'#8E8E93',marginTop:2}}>Search by mobile & send WhatsApp payment link</div>
          </div>
          <button onClick={onClose} style={{marginLeft:'auto',width:28,height:28,borderRadius:'50%',border:'none',background:'rgba(0,0,0,.07)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,color:'#636366',flexShrink:0}}>×</button>
        </div>

        {/* Phone input */}
        <div style={{marginBottom:16}}>
          <label style={{fontSize:12,fontWeight:600,color:'#48484a',display:'block',marginBottom:6}}>Customer Mobile Number</label>
          <div style={{display:'flex',gap:8}}>
            <input type="tel" value={phone}
              onChange={e=>{setPhone(e.target.value);setResult(null);setNotFound(false);setSent(false);}}
              onKeyDown={e=>e.key==='Enter'&&handleSearch()}
              placeholder="e.g. 9876543210" maxLength={10}
              style={{flex:1,padding:'11px 14px',borderRadius:12,border:'1.5px solid rgba(0,0,0,.12)',fontSize:15,fontFamily:'inherit',background:'#f9f9f9',outline:'none',letterSpacing:.5}}
            />
            <button onClick={handleSearch} disabled={searching||phone.trim().length<10} style={{
              padding:'11px 18px',borderRadius:12,border:'none',
              background:phone.trim().length>=10?'linear-gradient(135deg,#007AFF,#0056d3)':'#e5e5ea',
              color:phone.trim().length>=10?'#fff':'#8E8E93',
              fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',
              display:'flex',alignItems:'center',gap:6,
              boxShadow:phone.trim().length>=10?'0 4px 12px rgba(0,122,255,.3)':'none',
            }}>
              {searching
                ? <div style={{width:14,height:14,borderRadius:'50%',border:'2px solid rgba(255,255,255,.3)',borderTopColor:'#fff',animation:'rcSpin .7s linear infinite'}}/>
                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              }
              Search
            </button>
          </div>
        </div>

        {/* Not found */}
        {notFound && (
          <div style={{padding:'16px',borderRadius:14,background:'rgba(255,59,48,.05)',border:'1px solid rgba(255,59,48,.15)',textAlign:'center',color:'#FF3B30',fontSize:13,fontWeight:500,marginBottom:16}}>
            No device found for this mobile number
          </div>
        )}

        {/* Result — customer info + device picker + plans */}
        {result && !sent && (() => {
          const firstDevice = result[0];
          const activeDevice = selDevice || (result.length === 1 ? result[0] : null);
          return (
            <div style={{animation:'rcFadeUp .3s ease'}}>
              {/* Customer info */}
              <div style={{background:'linear-gradient(135deg,#f0f8ff,#fff)',border:'1.5px solid rgba(0,122,255,.2)',borderRadius:16,padding:'16px 18px',marginBottom:12}}>
                <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:result.length>1?12:0}}>
                  <div style={{width:42,height:42,borderRadius:13,flexShrink:0,background:'linear-gradient(135deg,#DC2626,#EF4444)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:16,fontWeight:800}}>
                    {(firstDevice.Enduser_name||firstDevice.cust_name||'?')[0].toUpperCase()}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:15,fontWeight:700,color:'#1c1c1e'}}>{firstDevice.Enduser_name||firstDevice.cust_name||'Unknown'}</div>
                    <div style={{fontSize:11,color:'#8E8E93'}}>Registered Customer · {result.length} device{result.length>1?'s':''}</div>
                  </div>
                  <div style={{fontSize:10,fontWeight:700,padding:'3px 10px',borderRadius:20,background:'rgba(255,59,48,.1)',color:'#FF3B30'}}>OFFLINE</div>
                </div>
                {/* Device picker — show when multiple devices */}
                {result.length > 1 && (
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:'#8E8E93',letterSpacing:.4,marginBottom:8}}>SELECT DEVICE TO RECHARGE</div>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {result.map((d,i) => (
                        <div key={i} onClick={()=>setSelDevice(d)} style={{
                          display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:12,
                          cursor:'pointer',
                          border:activeDevice===d?'2px solid #007AFF':'1.5px solid rgba(0,0,0,.08)',
                          background:activeDevice===d?'rgba(0,122,255,.06)':'rgba(0,0,0,.02)',
                        }}>
                          <div style={{width:32,height:32,borderRadius:10,background:activeDevice===d?'linear-gradient(135deg,#007AFF,#0056d3)':'linear-gradient(135deg,#e5e5ea,#d1d1d6)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                              <path d="M12 2a3 3 0 100 6 3 3 0 000-6zM19 21a7 7 0 10-14 0"/>
                            </svg>
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:'#1c1c1e'}}>{d.friendly_name||d.friendlyname}</div>
                            <div style={{fontSize:10,color:'#8E8E93',fontFamily:'monospace'}}>{(d.mac_id||d.macid||'').toUpperCase()}</div>
                          </div>
                          {activeDevice===d && <CheckCircle size={16} color="#007AFF"/>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Single device info */}
                {result.length === 1 && (
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:7,marginTop:12}}>
                    {[['📞 Phone',firstDevice.phone_no||firstDevice.phone],['📍 Location',firstDevice.location],['📱 Device',firstDevice.friendly_name||firstDevice.friendlyname],['🔧 Model',firstDevice.model_no]].filter(([,v])=>v).map(([label,value])=>(
                      <div key={label} style={{padding:'8px 10px',background:'rgba(0,0,0,.025)',borderRadius:10}}>
                        <div style={{fontSize:9,color:'#8E8E93',fontWeight:600,marginBottom:2}}>{label}</div>
                        <div style={{fontSize:12,color:'#1c1c1e',fontWeight:600}}>{value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Plans — only show if device selected */}
              {activeDevice ? (
                <>
                  <div style={{fontSize:13,fontWeight:600,color:'#48484a',marginBottom:10}}>
                    Send recharge link for <strong>{activeDevice.friendly_name||activeDevice.friendlyname}</strong> via WhatsApp:
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {PLANS.filter(p=>p.id!=='test').map(plan=>(
                      <div key={plan.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderRadius:14,border:`1.5px solid ${plan.color}22`,background:`${plan.color}06`,position:'relative',overflow:'hidden'}}>
                        <div style={{position:'absolute',left:0,top:0,bottom:0,width:4,background:`linear-gradient(180deg,${plan.color},${plan.accent})`,borderRadius:'4px 0 0 4px'}}/>
                        <div style={{paddingLeft:8}}>
                          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:2}}>
                            <span style={{fontSize:14,fontWeight:700,color:'#1c1c1e'}}>{plan.label}</span>
                            {plan.badge&&<span style={{fontSize:9,fontWeight:700,padding:'1px 7px',borderRadius:20,background:`linear-gradient(135deg,${plan.color},${plan.accent})`,color:'#fff'}}>{plan.badge}</span>}
                          </div>
                          <div style={{fontSize:11,color:'#8E8E93'}}>{plan.duration}</div>
                          <div style={{fontSize:19,fontWeight:900,color:plan.color,marginTop:2}}>\u20b9{plan.price}</div>
                        </div>
                        <button onClick={()=>{
                          const ph   = (activeDevice.phone_no||activeDevice.phone||phone).replace(/\D/g,'').slice(-10);
                          const name = activeDevice.Enduser_name||activeDevice.cust_name||'Customer';
                          const dev  = activeDevice.friendly_name||activeDevice.friendlyname||activeDevice.mac_id||'';
                          const link = PLAN_LINKS[plan.id]||PLAN_LINKS['3m'];
                          const msg  = encodeURIComponent(
                            `Hi ${name}!\n\nYour Havells Water Purifier (${dev}) subscription needs renewal.\n\n`+
                            `Plan: ${plan.label} - \u20b9${plan.price}\nPay here: ${link}\n\n`+
                            `Your purifier will be automatically activated within minutes of payment.\n\nTeam Havells`
                          );
                          window.open(`https://wa.me/91${ph}?text=${msg}`,'_blank','noopener,noreferrer');
                          setSelPlan(plan); setSent(true);
                        }} style={{padding:'9px 16px',borderRadius:11,border:'none',background:'linear-gradient(135deg,#25D366,#128C7E)',color:'#fff',fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:6,boxShadow:'0 3px 10px rgba(37,211,102,.4)'}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d={WA_PATH}/></svg>
                          Send
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{textAlign:'center',padding:'16px',background:'rgba(0,122,255,.05)',borderRadius:12,fontSize:13,color:'#007AFF',fontWeight:500}}>
                  \u2191 Select a device above to send the payment link
                </div>
              )}
            </div>
          );
        })()}

        {/* Sent confirmation */}
        {result && sent && (
          <div style={{textAlign:'center',padding:'24px 20px',background:'linear-gradient(135deg,rgba(37,211,102,.08),rgba(18,140,126,.05))',border:'1.5px solid rgba(37,211,102,.25)',borderRadius:18}}>
            <div style={{width:64,height:64,borderRadius:'50%',margin:'0 auto 16px',background:'linear-gradient(135deg,#25D366,#128C7E)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 6px 20px rgba(37,211,102,.4)'}}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div style={{fontSize:16,fontWeight:800,color:'#1c1c1e',marginBottom:6}}>WhatsApp Opened!</div>
            <div style={{fontSize:13,color:'#8E8E93',lineHeight:1.6,marginBottom:16}}>
              Payment link for <strong>{selPlan?.label}</strong> (₹{selPlan?.price}) sent to<br/>
              <strong>{(selDevice||result?.[0])?.Enduser_name||(selDevice||result?.[0])?.cust_name}</strong>
            </div>
            <div style={{fontSize:11,color:'#8E8E93',padding:'10px 14px',background:'rgba(0,0,0,.04)',borderRadius:10}}>
              Device will activate automatically within minutes of payment
            </div>
            <button onClick={()=>{setPhone('');setResult(null);setSent(false);setSelPlan(null);setSelDevice(null);}} style={{marginTop:16,padding:'10px 24px',borderRadius:12,border:'none',background:'#1c1c1e',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>
              Search Another
            </button>
          </div>
        )}

        {!result && !notFound && (
          <div style={{textAlign:'center',padding:'16px 0 4px',color:'#aeaeb2',fontSize:12}}>
            Enter the customer's registered mobile number to find their device
          </div>
        )}
      </div>
    </div>
  );
}

// ── Merge default PLANS with a custom pricing row from the plan table ──────
function mergePlanPricing_REMOVED(basePlans, customRow) {
  if (!customRow) return basePlans;
  const map = { test:'price_test', '3m':'price_3m', '6m':'price_6m', '1y':'price_12m' };
  // 1-month custom pricing has no matching default tier, so it's only
  // surfaced if you add a '1m' plan later. For now we map the 4 existing
  // tiers onto the matching custom price fields.
  return basePlans.map(p => {
    const key = map[p.id];
    if (key && customRow[key] != null) {
      return { ...p, price: Number(customRow[key]) };
    }
    return p;
  });
}

// ── Main Component ───────────────────────────────────────────────────
export default function RechargeManagement({ userId, isAdmin }) {
  const [devices,     setDevices]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [isPinging,   setIsPinging]   = useState(false);
  const [selected,    setSelected]    = useState(null);
  const [selPlan,     setSelPlan]     = useState(null);
  const [fetchError,  setFetchError]  = useState('');
  const [rcResp,      setRcResp]      = useState(null);
  const [showOk,      setShowOk]      = useState(false);
  const [showOffline, setShowOffline] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [customPricing, setCustomPricing] = useState(null); // matched plan row for `selected`
  const [pricingLoading, setPricingLoading] = useState(false);
  const timersRef = useRef({});
  const { connected, subscribe, publish } = useMqttProxy();

  const fetchDevices = useCallback(async () => {
    if (!isAdmin && !userId) return;
    setLoading(true); setFetchError('');
    try {
      const res  = await fetch(`${API_BASE}/api/admin-devices`, { headers:{'ngrok-skip-browser-warning':'true'} });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const raw  = await res.json();
      const data = Array.isArray(raw) ? raw : (raw.registrations??raw.data??raw.devices??[]);
      const seen = new Set();
      const unique = data.map(normaliseDevice).filter(d => {
        const k = d.macid.toUpperCase();
        if (seen.has(k)) return false;
        seen.add(k); return true;
      });
      setDevices(unique);
    } catch(e) { setFetchError(e.message); }
    finally    { setLoading(false); }
  }, [userId, isAdmin]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const pingAll = useCallback(() => {
    if (!connected || devices.length === 0) return;
    setIsPinging(true);
    devices.forEach(device => {
      const pub = publishTopic(device.custname, device.macid, device.friendlyname);
      const ack = ackTopic(device.custname, device.macid, device.friendlyname);
      setDevices(prev => prev.map(d => d.macid===device.macid ? {...d,_status:'pinging'} : d));
      const unsub = subscribe(ack, (msg) => {
        const m = typeof msg==='string' ? msg : JSON.stringify(msg);
        if (m.toLowerCase().includes('ack')) {
          clearTimeout(timersRef.current[device.macid]);
          setDevices(prev => prev.map(d => d.macid===device.macid ? {...d,_status:'active'} : d));
          unsub();
        }
      });
      setTimeout(() => publish(pub, 'ping'), 200);
      timersRef.current[device.macid] = setTimeout(() => {
        setDevices(prev => prev.map(d => d.macid===device.macid&&d._status==='pinging' ? {...d,_status:'inactive'} : d));
        unsub();
      }, PING_TIMEOUT);
    });
  }, [connected, devices, subscribe, publish]);

  useEffect(() => { if (!loading && devices.length>0 && connected) pingAll(); }, [loading, connected]); // eslint-disable-line
  useEffect(() => { if (isPinging && devices.every(d=>d._status!=='pinging')) setIsPinging(false); }, [devices, isPinging]);
  useEffect(() => () => Object.values(timersRef.current).forEach(clearTimeout), []);
  useEffect(() => {
    if (selected) {
      const u = devices.find(d => d.macid===selected.macid);
      if (u && u._status!=='active') setSelected(null);
    }
  }, [devices, selected]);

  // ── Fetch custom pricing for the selected device's model+region ──────
  // Looks up: GET ?resource=plan&model_no=X&region=Y  (on the merged Lambda
  // at g3mu2eke0l, NOT the old havells_plan endpoint).
  // This MUST resolve before the plan cards render so admin never sees a
  // flash of default pricing that then silently changes underneath them.
  const [pricingChecked, setPricingChecked] = useState(false);

  useEffect(() => {
    if (!selected || !selected.prodmodel || !selected.location) {
      setCustomPricing(null);
      setPricingChecked(!!selected); // if a device is picked but has no model/location, still unblock the UI
      return;
    }
    setPricingLoading(true);
    setPricingChecked(false);
    const region = selected.location.trim();
    const url = `${PLAN_API_BASE}&model_no=${encodeURIComponent(selected.prodmodel)}&region=${encodeURIComponent(region)}`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        const plan = data.plan;
        setCustomPricing(plan && plan.model_no ? plan : null);
      })
      .catch(() => setCustomPricing(null))
      .finally(() => { setPricingLoading(false); setPricingChecked(true); });
  }, [selected]);

  const handlePay = (plan) => {
    if (!selected) return;
    const mac      = selected.macid.replace(/[^a-fA-F0-9]/g,'').slice(0,12).toLowerCase();
    const txn      = Math.floor(Math.random()*0xFFFFF).toString(16).toUpperCase().padStart(5,'0');
    const addDays  = n => { const d=new Date(); d.setDate(d.getDate()+n); return String(d.getDate()).padStart(2,'0')+String(d.getMonth()+1).padStart(2,'0')+String(d.getFullYear()).slice(-2); };
    // litres/days now travel directly on the plan object (dynamic, from
    // PLAN_CONFIG via toCardPlan) instead of separate lookup maps keyed by id.
    const litres   = plan.litres || '30';
    const validity = addDays(plan.days || 90);
    const cmdTopic = `/hoags/livpure/lotier/waas/${mac}/`;
    const rspTopic = `/hoags/livpure/lotier/waas/response/${mac}/`;
    setRcResp(null); setShowOk(false);

    const loadRazorpay = () => new Promise(resolve => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload  = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

    loadRazorpay().then(loaded => {
      if (!loaded) { window.open(PLAN_LINKS[plan.id]||'', '_blank', 'noopener,noreferrer'); return; }
      // plan.price already reflects custom pricing if set (or the fallback
      // default otherwise) — just convert straight to paise.
      const amountPaise = Math.round((plan.price || 0) * 100) || 29900;
      const options = {
        key: RAZORPAY_KEY,
        amount: amountPaise,
        currency: 'INR',
        name: 'Havells Water Purifier',
        description: `${plan.label} Subscription — ${selected.friendlyname}`,
        image: '/havells-logo.png',
        notes: { device_mac:mac, device_name:selected.friendlyname, plan:plan.label, litres, validity, txn_id:txn },
        theme: { color:'#DC2626' },
        handler: function(response) {
          const rzpId      = response.razorpay_payment_id || '';
          const txnFinal   = rzpId.slice(-4).toUpperCase() || txn;
          const finalPayload = { IM:mac, CMD:'RC', L:litres, V:validity, CF:'Y', X:txnFinal, OW:'N' };
          const unsub = subscribe(rspTopic, (msg) => {
            try {
              const json = typeof msg==='string' ? JSON.parse(msg) : msg;
              if (json.CMD==='RC') {
                setRcResp(json); setShowOk(true);
                const hist = JSON.parse(localStorage.getItem('hoags_txn_history')||'[]');
                hist.unshift({ type:'RC', device:selected.friendlyname, mac, plan:plan.label, price:`₹${plan.price}`, razorpay_payment_id:rzpId, txn_id:txnFinal, response:json, time:new Date().toISOString() });
                localStorage.setItem('hoags_txn_history', JSON.stringify(hist.slice(0,100)));
                setTimeout(() => unsub(), 100);
              }
            } catch {}
          });
          publish(cmdTopic, JSON.stringify(finalPayload));
        },
        modal: { ondismiss: () => {} },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', e => console.error('[Razorpay]', e.error));
      rzp.open();
    });
  };

  const onlineDevices  = devices.filter(d => d._status==='active');
  const offlineDevices = devices.filter(d => d._status!=='active');
  const dynamicTiers    = buildDynamicPlans(customPricing) || FALLBACK_DYNAMIC_PLANS;
  const displayPlans    = [...PLANS, ...dynamicTiers.map(toCardPlan)];

  // Device search — filters the Select Device list by name, MAC, customer or location
  const matchesSearch = (d) => {
    if (!deviceSearch.trim()) return true;
    const q = deviceSearch.trim().toLowerCase();
    return [d.friendlyname, d.macid, d.custname, d.location, d.userid]
      .some(v => (v || '').toLowerCase().includes(q));
  };
  const visibleOnline  = onlineDevices.filter(matchesSearch);
  const visibleOffline = offlineDevices.filter(matchesSearch);

  return (
    <div style={{fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",maxWidth:900}}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{marginBottom:28}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
          <div>
            <img src="/havells-logo.png" alt="Havells" style={{height:48,objectFit:'contain',marginBottom:10,display:'block'}}/>
            <h1 style={{fontSize:28,fontWeight:800,color:'#1c1c1e',letterSpacing:-.6,margin:0}}>Recharge</h1>
            <p style={{color:'#8E8E93',fontSize:14,marginTop:4}}>Select an online device, then choose a plan</p>
          </div>
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <div style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:20,fontSize:12,fontWeight:600,background:connected?'rgba(52,199,89,.1)':'rgba(255,59,48,.08)',color:connected?'#34C759':'#FF3B30',border:`1px solid ${connected?'rgba(52,199,89,.2)':'rgba(255,59,48,.15)'}`}}>
              <span style={{width:6,height:6,borderRadius:'50%',background:connected?'#34C759':'#FF3B30',animation:connected?'rcPulse 1.5s ease-in-out infinite':'none'}}/>{connected?'MQTT Live':'MQTT Offline'}
            </div>
            <button onClick={()=>setShowOffline(true)} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 16px',borderRadius:12,border:'none',background:'linear-gradient(135deg,#25D366,#128C7E)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',boxShadow:'0 4px 14px rgba(37,211,102,.3)',fontFamily:'inherit'}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d={WA_PATH}/></svg>
              Offline Recharge
            </button>
            <button onClick={fetchDevices} disabled={loading} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 16px',borderRadius:12,border:'none',background:'linear-gradient(135deg,#007AFF,#0063CC)',color:'#fff',fontWeight:600,fontSize:13,cursor:'pointer',boxShadow:'0 4px 14px rgba(0,122,255,.28)',opacity:loading?.6:1,fontFamily:'inherit'}}>
              <RefreshCw size={13} style={loading?{animation:'rcSpin .8s linear infinite'}:{}}/> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Step 1 — Select Device */}
      <div style={{marginBottom:32}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
          <div style={{width:28,height:28,borderRadius:'50%',background:selected?'#34C759':'#007AFF',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:'#fff',flexShrink:0,transition:'background .3s'}}>
            {selected ? <CheckCircle size={15} color="#fff"/> : '1'}
          </div>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:'#1c1c1e'}}>Select Device</div>
            <div style={{fontSize:12,color:'#8E8E93'}}>{selected ? `Selected: ${selected.friendlyname}` : onlineDevices.length>0 ? `${onlineDevices.length} device${onlineDevices.length>1?'s':''} online — tap to select` : 'No devices online right now'}</div>
          </div>
        </div>

        {/* Device search — same pattern as Dashboard's device search */}
        <div style={{position:'relative', marginBottom:14}}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2.2" strokeLinecap="round"
            style={{position:'absolute', left:14, top:'50%', transform:'translateY(-50%)'}}>
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input type="text" value={deviceSearch} onChange={e=>setDeviceSearch(e.target.value)}
            placeholder="Search devices by name, MAC, customer or location…"
            style={{width:'100%', padding:'11px 14px 11px 38px', borderRadius:12,
              border:'1.5px solid rgba(0,0,0,.1)', fontSize:13, fontFamily:'inherit',
              background:'#fff', outline:'none', boxSizing:'border-box'}}/>
          {deviceSearch && (
            <button onClick={()=>setDeviceSearch('')} style={{position:'absolute', right:10, top:'50%',
              transform:'translateY(-50%)', background:'rgba(0,0,0,.07)', border:'none', borderRadius:'50%',
              width:20, height:20, cursor:'pointer', color:'#636366', fontSize:12, display:'flex',
              alignItems:'center', justifyContent:'center'}}>×</button>
          )}
        </div>
        {fetchError && <div style={{background:'rgba(255,59,48,.06)',border:'1px solid rgba(255,59,48,.15)',borderRadius:12,padding:'12px 16px',marginBottom:14,color:'#FF3B30',fontSize:13}}>Error: {fetchError}</div>}
        {loading ? (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {[1,2].map(i=>(<div key={i} style={{height:78,borderRadius:16,background:'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',backgroundSize:'600px 100%',animation:'rcShimmer 1.5s infinite linear'}}/>))}
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {visibleOnline.length===0 && (
              <div style={{textAlign:'center',padding:'32px 20px',background:'#fff',borderRadius:18,border:'1px solid rgba(0,0,0,.06)',color:'#8E8E93',fontSize:13}}>
                <WifiOff size={28} color="#C7C7CC" style={{marginBottom:10}}/>
                <p style={{fontWeight:500}}>{deviceSearch ? 'No devices match your search' : 'No devices online'}</p>
                <p style={{fontSize:11,marginTop:4}}>{deviceSearch ? 'Try a different name, MAC, or location' : 'Press Refresh to scan for devices'}</p>
              </div>
            )}
            {visibleOnline.map(d=>(
              <div key={d.macid} onClick={()=>setSelected(d)} style={{display:'flex',alignItems:'center',gap:14,padding:'14px 18px',borderRadius:14,cursor:'pointer',border:selected?.macid===d.macid?'2px solid #007AFF':'1.5px solid rgba(52,199,89,.25)',background:selected?.macid===d.macid?'rgba(0,122,255,.05)':'rgba(52,199,89,.04)',boxShadow:selected?.macid===d.macid?'0 0 0 3px rgba(0,122,255,.12)':'0 2px 8px rgba(0,0,0,.05)',animation:'rcFadeUp .3s ease both',position:'relative'}}>
                <div style={{position:'absolute',left:0,top:0,bottom:0,width:3,background:'linear-gradient(180deg,#34C759,#30D158)',borderRadius:'3px 0 0 3px'}}/>
                {selected?.macid===d.macid && <div style={{position:'absolute',top:10,right:12,width:20,height:20,borderRadius:'50%',background:'#007AFF',display:'flex',alignItems:'center',justifyContent:'center'}}><CheckCircle size={13} color="#fff"/></div>}
                <div style={{width:40,height:40,borderRadius:12,flexShrink:0,background:'linear-gradient(135deg,#34C759,#30D158)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 4px 12px rgba(52,199,89,.35)'}}><Droplets size={18} color="#fff"/></div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:14,color:'#1c1c1e',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.friendlyname}</div>
                  <div style={{fontSize:11,color:'#8E8E93',fontFamily:'monospace',marginTop:2}}>{d.macid.toUpperCase()} · {d.custname==='Livpure'?'Havells':d.custname}</div>
                </div>
              </div>
            ))}
            {visibleOffline.length>0 && (<>
              <div style={{fontSize:11,color:'#C7C7CC',fontWeight:600,letterSpacing:.4,margin:'6px 4px 2px',textTransform:'uppercase'}}>Offline / Scanning</div>
              {visibleOffline.map(d=><DeviceCard key={d.macid} device={d} selected={false} onClick={()=>{}}/>)}
            </>)}
          </div>
        )}
      </div>

      {/* Step 2 — Choose Plan */}
      <div>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:18}}>
          <div style={{width:28,height:28,borderRadius:'50%',background:selPlan?'#34C759':selected?'#007AFF':'#e5e5ea',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:selected?'#fff':'#C7C7CC',flexShrink:0,transition:'background .3s'}}>
            {selPlan ? <CheckCircle size={15} color="#fff"/> : '2'}
          </div>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:selected?'#1c1c1e':'#C7C7CC'}}>Choose a Plan</div>
            <div style={{fontSize:12,color:'#8E8E93'}}>
              {!selected ? 'Select a device first'
                : pricingLoading ? 'Checking for custom pricing…'
                : selPlan ? `${selPlan.label} selected — tap Pay to proceed`
                : 'Pick a subscription plan'}
            </div>
          </div>
        </div>

        {/* Pricing lookup banner — explicit result shown before plan cards render */}
        {selected && (
          pricingLoading ? (
            <div style={{marginBottom:16,padding:'12px 16px',borderRadius:12,background:'rgba(0,0,0,.03)',
              border:'1px solid rgba(0,0,0,.06)',display:'flex',alignItems:'center',gap:10,animation:'rcFadeUp .2s ease'}}>
              <Loader size={14} color="#8E8E93" style={{animation:'rcSpin .8s linear infinite'}}/>
              <span style={{fontSize:12,color:'#8E8E93'}}>
                Checking pricing for model <strong>{selected.prodmodel || '—'}</strong> in <strong>{selected.location || 'unknown region'}</strong>…
              </span>
            </div>
          ) : pricingChecked && customPricing ? (
            <div style={{marginBottom:16,padding:'12px 16px',borderRadius:12,background:'rgba(255,159,10,.08)',
              border:'1px solid rgba(255,159,10,.2)',display:'flex',alignItems:'center',gap:10,animation:'rcFadeUp .2s ease'}}>
              <span style={{fontSize:16}}>🏷️</span>
              <span style={{fontSize:12,color:'#E6870A',fontWeight:600}}>
                Custom pricing for <strong>{selected.prodmodel}</strong> · <strong>{customPricing.region}</strong>
                {customPricing.updated_by_name ? ` · set by ${customPricing.updated_by_name}` : ''}
              </span>
            </div>
          ) : pricingChecked ? (
            <div style={{marginBottom:16,padding:'12px 16px',borderRadius:12,background:'rgba(0,0,0,.03)',
              border:'1px solid rgba(0,0,0,.06)',display:'flex',alignItems:'center',gap:10,animation:'rcFadeUp .2s ease'}}>
              <span style={{fontSize:14}}>ℹ️</span>
              <span style={{fontSize:12,color:'#8E8E93'}}>
                No custom pricing set for <strong>{selected.prodmodel || 'this model'}</strong>
                {selected.location ? <> in <strong>{selected.location}</strong></> : ''} — showing default prices
              </span>
            </div>
          ) : null
        )}

        {/* Plan cards — only rendered once the pricing check has resolved (or no device selected yet) */}
        {(!selected || pricingChecked) && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:16}}>
            {displayPlans.map((plan,i)=>(
              <div key={plan.id} style={{animationDelay:`${i*0.07}s`}}>
                <PlanCard plan={plan} selected={selPlan} disabled={!selected} onSelect={setSelPlan} onPay={handlePay}/>
              </div>
            ))}
          </div>
        )}
        {selected && pricingLoading && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:16}}>
            {[1,2,3,4].map(i=>(
              <div key={i} style={{height:280,borderRadius:20,background:'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',backgroundSize:'600px 100%',animation:'rcShimmer 1.5s infinite linear'}}/>
            ))}
          </div>
        )}

        {selected && selPlan && (
          <div style={{marginTop:20,padding:'14px 18px',borderRadius:14,background:'rgba(0,122,255,.06)',border:'1px solid rgba(0,122,255,.15)',display:'flex',alignItems:'center',gap:12,animation:'rcFadeUp .3s ease'}}>
            <CheckCircle size={18} color="#007AFF"/>
            <div style={{fontSize:13,color:'#007AFF',fontWeight:500}}>Paying <strong>₹{selPlan.price}</strong> for <strong>{selPlan.label}</strong> on <strong>{selected.friendlyname}</strong> — tap <strong>Pay ₹{selPlan.price}</strong> on the card to open Razorpay.</div>
          </div>
        )}
      </div>

      {/* Success popup */}
      {showOk && rcResp && (
        <div onClick={()=>setShowOk(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(16px)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',animation:'rcFadeUp .2s ease'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:28,padding:'32px 28px',width:320,textAlign:'center',boxShadow:'0 32px 80px rgba(0,0,0,.25)',animation:'rcFadeUp .3s cubic-bezier(.34,1.56,.64,1)'}}>
            <div style={{width:72,height:72,borderRadius:'50%',margin:'0 auto 20px',background:'linear-gradient(135deg,#34C759,#30D158)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 8px 28px rgba(52,199,89,.4)'}}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div style={{fontSize:20,fontWeight:800,color:'#1c1c1e',letterSpacing:-.4,marginBottom:6}}>Payment Successful</div>
            <div style={{fontSize:13,color:'#8E8E93',marginBottom:20,lineHeight:1.6}}>{selected?.friendlyname} has been recharged</div>
            <div style={{background:'rgba(0,0,0,.03)',borderRadius:14,padding:'14px 16px',marginBottom:20,textAlign:'left'}}>
              {rcResp.NL && <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:12,color:'#8E8E93'}}>New Balance</span><span style={{fontSize:13,fontWeight:700,color:'#34C759'}}>💧 {rcResp.NL} L</span></div>}
              {rcResp.V  && <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}><span style={{fontSize:12,color:'#8E8E93'}}>Valid Until</span><span style={{fontSize:13,fontWeight:700,color:'#1c1c1e'}}>📅 {rcResp.V}</span></div>}
              {rcResp.X  && <div style={{display:'flex',justifyContent:'space-between'}}><span style={{fontSize:12,color:'#8E8E93'}}>Txn ID</span><span style={{fontSize:11,fontWeight:600,color:'#8E8E93',fontFamily:'monospace'}}>{rcResp.X}</span></div>}
            </div>
            <button onClick={()=>setShowOk(false)} style={{width:'100%',padding:'14px',borderRadius:14,border:'none',background:'linear-gradient(135deg,#34C759,#30D158)',color:'#fff',fontWeight:700,fontSize:16,cursor:'pointer',fontFamily:'inherit',boxShadow:'0 4px 16px rgba(52,199,89,.4)'}}>Done</button>
          </div>
        </div>
      )}

      {/* Offline Recharge Modal */}
      {showOffline && <OfflineRechargeModal onClose={()=>setShowOffline(false)}/>}
    </div>
  );
}
